import { useMemo } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { formatCurrency } from '@shared/SharedAccountBody';
import { RetailAccountContent } from '@shared/types';
import { COLORS } from '../../constants/theme';
import { DataCard } from '@/components/nq/DataCard';

const SLICE_COLORS = [
  COLORS.primaryBlue,
  COLORS.primaryOrange,
  '#2ee66b',
  '#a78bfa',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#f472b6',
  '#94a3b8',
];

const TOP_N = 8;

interface PortfolioAllocationChartProps {
  content?: RetailAccountContent;
  isUnfunded?: boolean;
}

interface AllocationSlice {
  name: string;
  value: number;
  weightPct: number;
  color: string;
  [key: string]: string | number;
}

const PortfolioAllocationChart = ({
  content,
  isUnfunded = false,
}: PortfolioAllocationChartProps) => {
  const { slices, investedTotal } = useMemo(() => {
    const positions = content?.positions ?? [];
    const invested =
      content?.stockValue ??
      positions.reduce((sum, p) => sum + (p.marketValue ?? 0), 0);

    if (invested <= 0 || positions.length === 0) {
      return { slices: [] as AllocationSlice[], investedTotal: invested };
    }

    const withWeights = positions
      .map((p) => ({
        name: p.symbol,
        value: p.marketValue ?? 0,
        weightPct: ((p.marketValue ?? 0) / invested) * 100,
      }))
      .filter((p) => p.value > 0)
      .sort((a, b) => b.value - a.value);

    const raw =
      withWeights.length <= TOP_N
        ? withWeights
        : [
            ...withWeights.slice(0, TOP_N),
            {
              name: 'Other',
              value: withWeights.slice(TOP_N).reduce((sum, p) => sum + p.value, 0),
              weightPct: 0,
            },
          ];

    const withOtherPct = raw.map((s, i) => ({
      ...s,
      weightPct:
        s.name === 'Other' && s.weightPct === 0
          ? (s.value / invested) * 100
          : s.weightPct,
      color: SLICE_COLORS[i % SLICE_COLORS.length],
    }));

    return { slices: withOtherPct, investedTotal: invested };
  }, [content]);

  const AllocationTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const item = payload[0].payload as AllocationSlice;
    return (
      <div className="pointer-events-none min-w-[172px] rounded-lg border border-border bg-[color:var(--nq-bg-deep)]/98 px-4 py-3.5 shadow-[var(--shadow-popover)]">
        <p className="mb-2.5 font-display text-sm font-bold leading-tight text-foreground">
          {item.name}
        </p>
        <div className="flex items-center justify-between gap-6 whitespace-nowrap py-1.5">
          <span className="text-xs text-muted-foreground">Weight</span>
          <span className="text-[13px] font-semibold tabular-nums text-foreground">
            {item.weightPct.toFixed(2)}%
          </span>
        </div>
        <div className="mt-1 flex items-center justify-between gap-6 whitespace-nowrap border-t border-[color:rgba(255,255,255,0.08)] pt-2">
          <span className="text-xs text-muted-foreground">Value</span>
          <span className="text-[13px] font-semibold tabular-nums text-foreground">
            {formatCurrency(item.value)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <DataCard title="Allocation" className="overflow-visible">
      {slices.length === 0 ? (
        <p className="m-0 text-sm text-muted-foreground">
          {isUnfunded
            ? 'Allocation will appear once you hold positions.'
            : 'No positions to chart.'}
        </p>
      ) : (
        <>
          <p className="-mt-2 mb-2.5 text-xs text-muted-foreground">
            Invested {formatCurrency(investedTotal)}
          </p>

          <div className="grid grid-cols-[112px_minmax(0,1fr)] items-start gap-x-3.5 gap-y-3">
            <div className="relative z-[1] h-28 w-28 shrink-0 leading-[0]">
              <ResponsiveContainer width={112} height={112}>
                <PieChart>
                  <Pie
                    data={slices}
                    dataKey="value"
                    nameKey="name"
                    cx={50}
                    cy={-40}
                    innerRadius={34}
                    outerRadius={52}
                    paddingAngle={2}
                    stroke={COLORS.darkBackground}
                    strokeWidth={2}
                    isAnimationActive={false}
                  >
                    {slices.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={entry.color}
                        stroke={COLORS.darkBackground}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    content={<AllocationTooltip />}
                    cursor={{ fill: 'rgba(255, 255, 255, 0.06)' }}
                    wrapperStyle={{ zIndex: 20, outline: 'none' }}
                    contentStyle={{
                      background: 'transparent',
                      border: 'none',
                      boxShadow: 'none',
                      padding: 0,
                      margin: 0,
                    }}
                    itemStyle={{ display: 'none' }}
                    labelStyle={{ display: 'none' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <ul
              className="m-0 flex max-h-[200px] list-none flex-col gap-1.5 overflow-y-auto p-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              aria-label="Position weights"
            >
              {slices.map((slice) => (
                <li
                  key={slice.name}
                  className="grid grid-cols-[10px_1fr_auto] items-center gap-1.5 text-xs leading-tight"
                >
                  <span
                    className="size-2.5 shrink-0 rounded-sm"
                    style={{ backgroundColor: slice.color }}
                    aria-hidden
                  />
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap font-medium text-foreground/85">
                    {slice.name}
                  </span>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {slice.weightPct.toFixed(1)}%
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </DataCard>
  );
};

export default PortfolioAllocationChart;
